import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, Lock, Send, Video } from "lucide-react";
import api, { formatApiError } from "../api";
import { useAuth } from "../context/AuthContext";

export function Matches() {
  const { user } = useAuth();
  const [matches, setMatches] = useState(null);
  const [gated, setGated] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/matches");
        setMatches(data.matches);
      } catch (err) {
        if (err?.response?.status === 403) setGated(true);
        else toast.error(formatApiError(err));
      }
    })();
  }, []);

  if (gated)
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-24 text-center" data-testid="matches-gated-overlay">
        <div className="card-surface p-10">
          <span className="mx-auto w-14 h-14 rounded-full bg-[#FEF3C7] border border-[#FDE68A] flex items-center justify-center text-[#92400E]">
            <Lock size={24} />
          </span>
          <h1 className="font-display text-3xl text-stone-900 mt-6">Messaging unlocks after verification</h1>
          <p className="text-stone-600 mt-4 max-w-md mx-auto">
            Once you've passed your video interview, your matches and messages will appear here.
          </p>
          <Link to="/verification" className="btn-accent mt-8" data-testid="matches-gated-verification-prompt-btn">
            <Video size={16} /> Book my video interview
          </Link>
        </div>
      </div>
    );

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
      <p className="eyebrow mb-2">Connections</p>
      <h1 className="font-display text-3xl sm:text-4xl text-stone-900 mb-8">Your matches</h1>
      {matches === null ? (
        <p className="text-stone-500">Loading…</p>
      ) : matches.length === 0 ? (
        <div className="card-surface p-12 text-center" data-testid="matches-empty-state">
          <p className="text-stone-600">No matches yet. Like a few verified members in Discover — when they like you back, you'll match.</p>
          <Link to="/discover" className="btn-primary mt-6" data-testid="matches-goto-discover-btn">
            Browse members
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {matches.map((m) => (
            <Link
              key={m.match_id}
              to={`/matches/${m.match_id}`}
              className="card-surface p-4 flex items-center gap-4 hover:shadow-md transition-shadow"
              data-testid={`match-card-${m.match_id}`}
            >
              <div className="w-14 h-14 rounded-2xl overflow-hidden bg-[#F3EFEA] shrink-0">
                {m.user.photo_url ? (
                  <img src={m.user.photo_url} alt={m.user.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-display text-2xl text-stone-300">
                    {m.user.name?.[0]}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-stone-800">
                    {m.user.name}, {m.user.age}
                  </p>
                  <BadgeCheck size={14} className="text-[#065F46]" />
                </div>
                <p className="text-sm text-stone-500 truncate">{m.last_message || "Say hello — you both liked each other."}</p>
              </div>
              <span className="text-xs text-stone-400">{m.user.location}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function ChatThread() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/matches/${matchId}/messages`);
      setData(data);
    } catch (err) {
      setError(formatApiError(err, "Could not load conversation."));
    }
  }, [matchId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [data?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await api.post(`/matches/${matchId}/messages`, { text: text.trim() });
      setText("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (error)
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <p className="text-stone-600" data-testid="chat-error-message">{error}</p>
        <button onClick={() => navigate("/matches")} className="btn-secondary mt-6" data-testid="chat-back-btn">
          Back to matches
        </button>
      </div>
    );

  if (!data) return <p className="text-center text-stone-500 py-24">Loading conversation…</p>;
  const other = data.other;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <div className="card-surface overflow-hidden" data-testid="chat-thread">
        <div className="px-5 py-4 border-b border-[#E7DFD5] flex items-center gap-3 bg-[#F3EFEA]/60">
          <div className="w-10 h-10 rounded-xl overflow-hidden bg-[#E7DFD5]">
            {other?.photo_url && <img src={other.photo_url} alt={other?.name} className="w-full h-full object-cover" />}
          </div>
          <div className="flex-1">
            <p className="font-semibold text-stone-800 text-sm">
              {other?.name}, {other?.age}
            </p>
            <p className="text-xs text-[#065F46] flex items-center gap-1">
              <BadgeCheck size={11} /> Both members video-interviewed
              {data.matched_at ? ` · Matched ${new Date(data.matched_at).toLocaleDateString()}` : ""}
            </p>
          </div>
          <Link to="/matches" className="text-xs text-stone-500 hover:text-stone-800" data-testid="chat-back-to-matches-link">
            All matches
          </Link>
        </div>

        <div className="h-[420px] overflow-y-auto p-5 space-y-3 bg-[#FAF8F5]">
          {data.messages.length === 0 && (
            <p className="text-center text-sm text-stone-400 py-16">
              You matched! Break the ice — ask about their favourite way to spend a Sunday.
            </p>
          )}
          {data.messages.map((m) => {
            const mine = m.sender_id === user.id;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    mine ? "bg-[#1F3A34] text-[#FAF8F5] rounded-br-sm" : "bg-white border border-[#E7DFD5] text-stone-700 rounded-bl-sm"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        <form onSubmit={send} className="p-4 border-t border-[#E7DFD5] flex gap-3">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={`Message ${other?.name?.split(" ")[0] || ""}…`}
            className="flex-1 rounded-xl border border-[#E7DFD5] bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A34]/30"
            data-testid="chat-message-input"
          />
          <button type="submit" className="btn-primary !px-5" data-testid="chat-send-btn">
            <Send size={15} />
          </button>
        </form>
      </div>
    </div>
  );
}

export default Matches;
